import { Input, TextArea } from '../../../shared/components/FormControls';
import { useState, useEffect, useMemo, useCallback } from 'react';
import '../../../shared/styles/Dice.css';
import '../../../shared/styles/Trialment.css';

const STORAGE_KEY = 'trialment-character-v1';

const ABILITY_SCORES = [
  { id: 'strength', label: 'Strength', short: 'STR' },
  { id: 'dexterity', label: 'Dexterity', short: 'DEX' },
  { id: 'constitution', label: 'Constitution', short: 'CON' },
  { id: 'intelligence', label: 'Intelligence', short: 'INT' },
  { id: 'wisdom', label: 'Wisdom', short: 'WIS' },
  { id: 'charisma', label: 'Charisma', short: 'CHA' },
];

const SKILLS = [
  { id: 'acrobatics', label: 'Acrobatics', ability: 'dexterity' },
  { id: 'animalHandling', label: 'Animal Handling', ability: 'wisdom' },
  { id: 'arcana', label: 'Arcana', ability: 'intelligence' },
  { id: 'athletics', label: 'Athletics', ability: 'strength' },
  { id: 'deception', label: 'Deception', ability: 'charisma' },
  { id: 'history', label: 'History', ability: 'intelligence' },
  { id: 'insight', label: 'Insight', ability: 'wisdom' },
  { id: 'intimidation', label: 'Intimidation', ability: 'charisma' },
  { id: 'investigation', label: 'Investigation', ability: 'intelligence' },
  { id: 'medicine', label: 'Medicine', ability: 'wisdom' },
  { id: 'nature', label: 'Nature', ability: 'intelligence' },
  { id: 'perception', label: 'Perception', ability: 'wisdom' },
  { id: 'performance', label: 'Performance', ability: 'charisma' },
  { id: 'persuasion', label: 'Persuasion', ability: 'charisma' },
  { id: 'religion', label: 'Religion', ability: 'intelligence' },
  { id: 'sleightOfHand', label: 'Sleight of Hand', ability: 'dexterity' },
  { id: 'stealth', label: 'Stealth', ability: 'dexterity' },
  { id: 'survival', label: 'Survival', ability: 'wisdom' },
];

const EQUIPMENT_SLOTS = [
  { id: 'helmet', label: 'Helmet', area: 'helmet' },
  { id: 'armorUpper', label: 'Upper Armor', area: 'armor-upper' },
  { id: 'armorLower', label: 'Lower Armor', area: 'armor-lower' },
  { id: 'fullArmor', label: 'Full Armor', area: 'full-armor' },
  { id: 'gloveLeft', label: 'Left Glove', area: 'glove-left' },
  { id: 'gloveRight', label: 'Right Glove', area: 'glove-right' },
  { id: 'amuletOne', label: 'Amulet I', area: 'amulet1' },
  { id: 'amuletTwo', label: 'Amulet II', area: 'amulet2' },
  { id: 'ringOne', label: 'Ring I', area: 'ring1' },
  { id: 'ringTwo', label: 'Ring II', area: 'ring2' },
  { id: 'boots', label: 'Boots', area: 'boots' },
];

const ACTION_SLOTS = [
  { id: 'spellOne', label: 'Spell / Stance I', category: 'spell' },
  { id: 'spellTwo', label: 'Spell / Stance II', category: 'spell' },
  { id: 'spellThree', label: 'Spell / Stance III', category: 'spell' },
  { id: 'spellFour', label: 'Spell / Stance IV', category: 'spell' },
  { id: 'weaponLeft', label: 'Weapon – Left Hand', category: 'weapon', hand: 'left' },
  { id: 'weaponRight', label: 'Weapon – Right Hand', category: 'weapon', hand: 'right' },
  { id: 'weaponThree', label: 'Weapon – Slot III', category: 'weapon' },
  { id: 'weaponFour', label: 'Weapon – Slot IV', category: 'weapon', supportsTwoHanded: true },
];

const DEFAULT_GENERAL = {
  name: '',
  ancestry: '',
  alignment: '',
  origin: '',
  background: '',
  pronouns: '',
  level: 1,
  experience: 0,
  maxHp: 0,
  currentHp: 0,
  tempHp: 0,
  baseArmorClass: 10,
  miscArmorClass: 0,
  baseSpeed: 6,
  miscSpeed: 0,
  initiativeBase: 0,
  miscInitiative: 0,
  proficiencyBonus: 2,
  carryWeight: '',
  initiativeNotes: '',
  traitNotes: '',
};

const DEFAULT_SKILL_STATE = {
  proficient: false,
  expertise: false,
  misc: 0,
};

const DEFAULT_EQUIPMENT_SLOT = () => ({
  name: '',
  notes: '',
  attuned: false,
  statBonuses: ABILITY_SCORES.reduce(
    (acc, ability) => ({ ...acc, [ability.id]: 0 }),
    {}
  ),
  armorClassBonus: 0,
  speedBonus: 0,
  initiativeBonus: 0,
});

const DEFAULT_ACTION_CARD = (slot) => ({
  id: slot.id,
  title: '',
  type: slot.category === 'spell' ? 'Spell / Stance' : 'Weapon',
  apCost: slot.category === 'spell' ? 1 : 1,
  damage: '',
  effect: '',
  requirements: '',
  hand: slot.hand ?? null,
  twoHanded: false,
  inAction: false,
});

const createAbilityMap = (value) =>
  ABILITY_SCORES.reduce((acc, ability) => ({ ...acc, [ability.id]: value }), {});

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return 0;
  }
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const calculateModifier = (score) => Math.floor((parseNumber(score) - 10) / 2);

const formatModifier = (modifier) => (modifier >= 0 ? `+${modifier}` : `${modifier}`);

export default function TrialmentCharacterSheet({ mode, diceBox }) {
  const [general, setGeneral] = useState(DEFAULT_GENERAL);
  const [baseAbilities, setBaseAbilities] = useState(() => createAbilityMap(10));
  const [skills, setSkills] = useState(() =>
    SKILLS.reduce((acc, skill) => {
      acc[skill.id] = { ...DEFAULT_SKILL_STATE };
      return acc;
    }, {})
  );
  const [equipmentSlots, setEquipmentSlots] = useState(() =>
    EQUIPMENT_SLOTS.reduce((acc, slot) => {
      acc[slot.id] = DEFAULT_EQUIPMENT_SLOT();
      return acc;
    }, {})
  );
  const [actionCards, setActionCards] = useState(() =>
    ACTION_SLOTS.map((slot) => DEFAULT_ACTION_CARD(slot))
  );
  const [portrait, setPortrait] = useState('');
  const [prepMode, setPrepMode] = useState(null);
  const [prepNotes, setPrepNotes] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      if (parsed.general) setGeneral({ ...DEFAULT_GENERAL, ...parsed.general });
      if (parsed.baseAbilities)
        setBaseAbilities({ ...createAbilityMap(10), ...parsed.baseAbilities });
      if (parsed.skills) {
        setSkills((prev) => ({
          ...prev,
          ...Object.keys(parsed.skills).reduce((acc, key) => {
            acc[key] = { ...DEFAULT_SKILL_STATE, ...parsed.skills[key] };
            return acc;
          }, {}),
        }));
      }
      if (parsed.equipmentSlots) {
        setEquipmentSlots((prev) => ({
          ...prev,
          ...Object.keys(parsed.equipmentSlots).reduce((acc, key) => {
            acc[key] = {
              ...DEFAULT_EQUIPMENT_SLOT(),
              ...parsed.equipmentSlots[key],
              statBonuses: {
                ...DEFAULT_EQUIPMENT_SLOT().statBonuses,
                ...(parsed.equipmentSlots[key]?.statBonuses ?? {}),
              },
            };
            return acc;
          }, {}),
        }));
      }
      if (parsed.actionCards) {
        setActionCards((prev) =>
          prev.map((card) =>
            parsed.actionCards.find((savedCard) => savedCard.id === card.id)
              ? {
                  ...card,
                  ...parsed.actionCards.find((savedCard) => savedCard.id === card.id),
                }
              : card
          )
        );
      }
      if (parsed.portrait) setPortrait(parsed.portrait);
      if (parsed.prepMode) setPrepMode(parsed.prepMode);
      if (parsed.prepNotes) setPrepNotes(parsed.prepNotes);
    } catch (error) {
      console.error('Failed to load Trialment character data', error);
    }
  }, []);

  useEffect(() => {
    const payload = {
      general,
      baseAbilities,
      skills,
      equipmentSlots,
      actionCards,
      portrait,
      prepMode,
      prepNotes,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [general, baseAbilities, skills, equipmentSlots, actionCards, portrait, prepMode, prepNotes]);

  const equipmentSummary = useMemo(() => {
    const abilityTotals = createAbilityMap(0);
    let armorClass = 0;
    let speed = 0;
    let initiative = 0;

    Object.values(equipmentSlots).forEach((slot) => {
      if (!slot) return;
      ABILITY_SCORES.forEach(({ id }) => {
        abilityTotals[id] += parseNumber(slot.statBonuses?.[id]);
      });
      armorClass += parseNumber(slot.armorClassBonus);
      speed += parseNumber(slot.speedBonus);
      initiative += parseNumber(slot.initiativeBonus);
    });

    return {
      abilityTotals,
      armorClass,
      speed,
      initiative,
    };
  }, [equipmentSlots]);

  const totalAbilities = useMemo(() => {
    const totals = createAbilityMap(0);
    ABILITY_SCORES.forEach(({ id }) => {
      totals[id] = parseNumber(baseAbilities[id]) + equipmentSummary.abilityTotals[id];
    });
    return totals;
  }, [baseAbilities, equipmentSummary.abilityTotals]);

  const abilityModifiers = useMemo(() => {
    return ABILITY_SCORES.reduce((acc, ability) => {
      acc[ability.id] = calculateModifier(totalAbilities[ability.id]);
      return acc;
    }, {});
  }, [totalAbilities]);

  const totalArmorClass = useMemo(() => {
    return (
      parseNumber(general.baseArmorClass) +
      parseNumber(general.miscArmorClass) +
      equipmentSummary.armorClass
    );
  }, [general.baseArmorClass, general.miscArmorClass, equipmentSummary.armorClass]);

  const totalSpeed = useMemo(() => {
    return (
      parseNumber(general.baseSpeed) +
      parseNumber(general.miscSpeed) +
      equipmentSummary.speed
    );
  }, [general.baseSpeed, general.miscSpeed, equipmentSummary.speed]);

  const initiativeScore = useMemo(() => {
    return (
      parseNumber(general.initiativeBase) +
      parseNumber(general.miscInitiative) +
      equipmentSummary.initiative +
      (abilityModifiers.dexterity ?? 0)
    );
  }, [general.initiativeBase, general.miscInitiative, equipmentSummary.initiative, abilityModifiers.dexterity]);

  const skillTotals = useMemo(() => {
    return SKILLS.reduce((acc, skill) => {
      const state = skills[skill.id] ?? DEFAULT_SKILL_STATE;
      const abilityModifier = abilityModifiers[skill.ability] ?? 0;
      const proficiencyMultiplier = state.expertise ? 2 : state.proficient ? 1 : 0;
      const total =
        abilityModifier +
        parseNumber(state.misc) +
        parseNumber(general.proficiencyBonus) * proficiencyMultiplier;

      acc[skill.id] = {
        total,
        abilityModifier,
        state,
      };
      return acc;
    }, {});
  }, [skills, abilityModifiers, general.proficiencyBonus]);

  const updateGeneral = useCallback((key, value) => {
    setGeneral((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAbilityChange = useCallback((abilityId, value) => {
    setBaseAbilities((prev) => ({ ...prev, [abilityId]: value }));
  }, []);

  const rollAbility = useCallback(
    (abilityId, method) => {
      const rollWithMathRandom = (times, die) =>
        Array.from({ length: times }, () => Math.floor(Math.random() * die) + 1).reduce(
          (sum, value) => sum + value,
          0
        );
      const expression = method === '3d6' ? '3d6' : '1d20';
      const result = method === '3d6' ? rollWithMathRandom(3, 6) : rollWithMathRandom(1, 20);

      if (diceBox && typeof diceBox.roll === 'function') {
        diceBox.roll(expression);
      }

      handleAbilityChange(abilityId, result);
    },
    [diceBox, handleAbilityChange]
  );

  const handlePortraitUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      setPortrait(result);
    };
    reader.readAsDataURL(file);
  };

  const handleEquipmentFieldChange = (slotId, field, value) => {
    setEquipmentSlots((prev) => ({
      ...prev,
      [slotId]: {
        ...prev[slotId],
        [field]: value,
      },
    }));
  };

  const handleEquipmentBonusChange = (slotId, abilityId, value) => {
    setEquipmentSlots((prev) => ({
      ...prev,
      [slotId]: {
        ...prev[slotId],
        statBonuses: {
          ...prev[slotId].statBonuses,
          [abilityId]: value,
        },
      },
    }));
  };

  const handleSkillToggle = (skillId, key) => {
    setSkills((prev) => ({
      ...prev,
      [skillId]: {
        ...prev[skillId],
        [key]: !prev[skillId][key],
      },
    }));
  };

  const handleSkillMiscChange = (skillId, value) => {
    setSkills((prev) => ({
      ...prev,
      [skillId]: {
        ...prev[skillId],
        misc: value,
      },
    }));
  };

  const handleActionCardChange = (cardId, key, value) => {
    setActionCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              [key]: key === 'apCost' ? parseNumber(value) : value,
            }
          : card
      )
    );
  };

  const toggleActionCardFlag = (cardId, flag) => {
    setActionCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              [flag]: !card[flag],
            }
          : card
      )
    );
  };

  const themeClass = mode === 'dark' ? 'trialment-dark' : '';

  return (
    <div className='page_content'>
      <div className={`trialment-character-sheet ${themeClass}`}>
        <section className='trialment-board trialment-skill-board'>
          <header className='trialment-board-header'>
            <h2 className='trialment-board-title'>Trialment Skill Board</h2>
            <div className='trialment-action-meta'>
              <span>Total AC: {totalArmorClass}</span>
              <span>Speed: {totalSpeed} steps</span>
              <span>Initiative: {initiativeScore}</span>
            </div>
          </header>

          <div className='trialment-info-grid'>
            <div className='trialment-info-field'>
              <label>Name</label>
              <Input
                className='trialment-text-input'
                value={general.name}
                onChange={(event) => updateGeneral('name', event.target.value)}
                placeholder='Character Name'
              />
            </div>
            <div className='trialment-info-field'>
              <label>Ancestry</label>
              <Input
                className='trialment-text-input'
                value={general.ancestry}
                onChange={(event) => updateGeneral('ancestry', event.target.value)}
                placeholder='Ancestry / Origin'
              />
            </div>
            <div className='trialment-info-field'>
              <label>Alignment</label>
              <Input
                className='trialment-text-input'
                value={general.alignment}
                onChange={(event) => updateGeneral('alignment', event.target.value)}
                placeholder='Alignment'
              />
            </div>
            <div className='trialment-info-field'>
              <label>Background</label>
              <Input
                className='trialment-text-input'
                value={general.background}
                onChange={(event) => updateGeneral('background', event.target.value)}
                placeholder='Background / Occupation'
              />
            </div>
            <div className='trialment-info-field'>
              <label>Pronouns</label>
              <Input
                className='trialment-text-input'
                value={general.pronouns}
                onChange={(event) => updateGeneral('pronouns', event.target.value)}
                placeholder='Pronouns'
              />
            </div>
            <div className='trialment-info-field'>
              <label>Level</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.level}
                onChange={(event) => updateGeneral('level', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Experience</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.experience}
                onChange={(event) => updateGeneral('experience', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Proficiency Bonus</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.proficiencyBonus}
                onChange={(event) => updateGeneral('proficiencyBonus', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>HP (Current)</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.currentHp}
                onChange={(event) => updateGeneral('currentHp', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>HP (Max)</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.maxHp}
                onChange={(event) => updateGeneral('maxHp', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Temporary HP</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.tempHp}
                onChange={(event) => updateGeneral('tempHp', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Base Armor Class</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.baseArmorClass}
                onChange={(event) => updateGeneral('baseArmorClass', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Misc AC Bonus</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.miscArmorClass}
                onChange={(event) => updateGeneral('miscArmorClass', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Base Speed (steps)</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.baseSpeed}
                onChange={(event) => updateGeneral('baseSpeed', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Misc Speed Δ</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.miscSpeed}
                onChange={(event) => updateGeneral('miscSpeed', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Initiative Base</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.initiativeBase}
                onChange={(event) => updateGeneral('initiativeBase', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Misc Initiative Δ</label>
              <Input
                className='trialment-number-input'
                type='number'
                inputMode='numeric'
                value={general.miscInitiative}
                onChange={(event) => updateGeneral('miscInitiative', Number(event.target.value))}
              />
            </div>
            <div className='trialment-info-field'>
              <label>Carry Weight / Load</label>
              <Input
                className='trialment-text-input'
                value={general.carryWeight}
                onChange={(event) => updateGeneral('carryWeight', event.target.value)}
                placeholder='Encumbrance or carried items'
              />
            </div>
          </div>

          <div className='trialment-info-field'>
            <label>Initiative Notes</label>
            <TextArea
              value={general.initiativeNotes}
              onChange={(event) => updateGeneral('initiativeNotes', event.target.value)}
              placeholder='Document initiative formula, step cost, and dodge odds.'
            />
          </div>

          <div className='trialment-info-field'>
            <label>Roleplay Traits & Hooks</label>
            <TextArea
              value={general.traitNotes}
              onChange={(event) => updateGeneral('traitNotes', event.target.value)}
              placeholder='Ideals, bonds, flaws, or Trialment-specific hooks.'
            />
          </div>

          <div className='trialment-character-portrait'>
            {portrait ? (
              <img src={portrait} alt='Character portrait' />
            ) : (
              <div className='trialment-portrait-placeholder' />
            )}
            <label className='trialment-portrait-upload'>
              Upload Portrait
              <Input type='file' accept='image/*' hidden onChange={handlePortraitUpload} />
            </label>
          </div>

          <div className='trialment-abilities-ring'>
            {ABILITY_SCORES.map((ability) => (
              <div key={ability.id} className='trialment-ability-card'>
                <h4>{ability.label}</h4>
                <div className='trialment-ability-summary'>
                  <span className='trialment-ability-score'>{totalAbilities[ability.id]}</span>
                  <span className='trialment-ability-modifier'>
                    {formatModifier(abilityModifiers[ability.id])}
                  </span>
                </div>
                <div className='trialment-info-field'>
                  <label>Base Score</label>
                  <Input
                    className='trialment-number-input'
                    type='number'
                    inputMode='numeric'
                    value={baseAbilities[ability.id]}
                    onChange={(event) => handleAbilityChange(ability.id, Number(event.target.value))}
                  />
                </div>
                <div className='trialment-info-field'>
                  <label>Gear Bonus</label>
                  <Input
                    className='trialment-number-input'
                    type='number'
                    inputMode='numeric'
                    value={equipmentSummary.abilityTotals[ability.id]}
                    readOnly
                  />
                </div>
                <div className='trialment-ability-actions'>
                  <button
                    type='button'
                    className='trialment-pill-button'
                    onClick={() => rollAbility(ability.id, '1d20')}
                  >
                    Roll d20
                  </button>
                  <button
                    type='button'
                    className='trialment-pill-button'
                    onClick={() => rollAbility(ability.id, '3d6')}
                  >
                    Roll 3d6
                  </button>
                  <button
                    type='button'
                    className='trialment-pill-button'
                    onClick={() => handleAbilityChange(ability.id, 10)}
                  >
                    Reset 10
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className='trialment-skills-grid'>
            {SKILLS.map((skill) => {
              const record = skillTotals[skill.id];
              const state = skills[skill.id];
              return (
                <div key={skill.id} className='trialment-skill-card'>
                  <div className='trialment-skill-meta'>
                    <strong>{skill.label}</strong>
                    <span>
                      {skill.ability.toUpperCase()} {formatModifier(record.abilityModifier)}
                    </span>
                    <div className='trialment-skill-controls'>
                      <label className='trialment-toggle'>
                        <Input
                          type='checkbox'
                          checked={state.proficient}
                          onChange={() => handleSkillToggle(skill.id, 'proficient')}
                        />
                        Prof.
                      </label>
                      <label className='trialment-toggle'>
                        <Input
                          type='checkbox'
                          checked={state.expertise}
                          onChange={() => handleSkillToggle(skill.id, 'expertise')}
                        />
                        Expert
                      </label>
                    </div>
                  </div>
                  <div className='trialment-info-field'>
                    <label>Misc</label>
                    <Input
                      className='trialment-number-input'
                      type='number'
                      inputMode='numeric'
                      value={state.misc}
                      onChange={(event) => handleSkillMiscChange(skill.id, Number(event.target.value))}
                    />
                    <div className='trialment-ability-modifier'>Total {formatModifier(record.total)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className='trialment-board trialment-equipment-board'>
          <header className='trialment-board-header'>
            <h2 className='trialment-board-title'>Equipment Board</h2>
            <span className='trialment-text-muted'>Mark 3-4 in-action items before initiative.</span>
          </header>

          <div className='trialment-equipment-layout'>
            <div className='trialment-equipment-figure' />
            {EQUIPMENT_SLOTS.map((slot) => {
              const data = equipmentSlots[slot.id];
              return (
                <div
                  key={slot.id}
                  className='trialment-equipment-slot'
                  style={{ gridArea: slot.area }}
                >
                  <h5>{slot.label}</h5>
                  <Input
                    className='trialment-text-input'
                    value={data.name}
                    onChange={(event) => handleEquipmentFieldChange(slot.id, 'name', event.target.value)}
                    placeholder='Item name'
                  />
                  <label className='trialment-toggle'>
                    <Input
                      type='checkbox'
                      checked={data.attuned}
                      onChange={() =>
                        handleEquipmentFieldChange(slot.id, 'attuned', !data.attuned)
                      }
                    />
                    In Action
                  </label>
                  <TextArea
                    value={data.notes}
                    onChange={(event) => handleEquipmentFieldChange(slot.id, 'notes', event.target.value)}
                    placeholder='Properties, weight, synergy requirements.'
                  />
                  <div className='trialment-equipment-bonuses'>
                    {ABILITY_SCORES.map((ability) => (
                      <Input
                        key={ability.id}
                        className='trialment-number-input'
                        type='number'
                        inputMode='numeric'
                        value={data.statBonuses[ability.id]}
                        onChange={(event) =>
                          handleEquipmentBonusChange(slot.id, ability.id, Number(event.target.value))
                        }
                        placeholder={ability.short}
                        title={`${ability.label} bonus`}
                      />
                    ))}
                    <Input
                      className='trialment-number-input'
                      type='number'
                      inputMode='numeric'
                      value={data.armorClassBonus}
                      onChange={(event) =>
                        handleEquipmentFieldChange(slot.id, 'armorClassBonus', Number(event.target.value))
                      }
                      placeholder='AC'
                    />
                    <Input
                      className='trialment-number-input'
                      type='number'
                      inputMode='numeric'
                      value={data.speedBonus}
                      onChange={(event) =>
                        handleEquipmentFieldChange(slot.id, 'speedBonus', Number(event.target.value))
                      }
                      placeholder='SPD'
                    />
                    <Input
                      className='trialment-number-input'
                      type='number'
                      inputMode='numeric'
                      value={data.initiativeBonus}
                      onChange={(event) =>
                        handleEquipmentFieldChange(
                          slot.id,
                          'initiativeBonus',
                          Number(event.target.value)
                        )
                      }
                      placeholder='INIT'
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className='trialment-board trialment-action-board'>
          <header className='trialment-board-header'>
            <h2 className='trialment-board-title'>Action Board</h2>
            <div className='trialment-action-toolbar'>
              <button
                type='button'
                onClick={() => setPrepMode('full')}
                className={prepMode === 'full' ? 'active' : undefined}
              >
                Think this through (2 AP)
              </button>
              <button
                type='button'
                onClick={() => setPrepMode('items')}
                className={prepMode === 'items' ? 'active' : undefined}
              >
                No, This (items only)
              </button>
              <button type='button' onClick={() => setPrepMode(null)}>
                Clear Prep
              </button>
            </div>
          </header>

          <div className='trialment-info-field'>
            <label>Preparation Notes</label>
            <TextArea
              value={prepNotes}
              onChange={(event) => setPrepNotes(event.target.value)}
              placeholder='Track which spells, stances, and items are currently in action.'
            />
          </div>

          <div className='trialment-action-grid'>
            {actionCards.map((card) => (
              <div key={card.id} className='trialment-action-card'>
                <div className='trialment-action-card-header'>
                  <h5>{card.label}</h5>
                  <div className='trialment-action-meta'>
                    <span>{card.type}</span>
                    <span>{card.apCost} AP</span>
                    {card.hand && <span>{card.hand === 'left' ? 'LH' : 'RH'}</span>}
                  </div>
                </div>
                <Input
                  className='trialment-text-input'
                  value={card.title}
                  onChange={(event) => handleActionCardChange(card.id, 'title', event.target.value)}
                  placeholder='Action name'
                />
                <Input
                  className='trialment-number-input'
                  type='number'
                  inputMode='numeric'
                  value={card.apCost}
                  onChange={(event) => handleActionCardChange(card.id, 'apCost', event.target.value)}
                  placeholder='AP cost'
                />
                <Input
                  className='trialment-text-input'
                  value={card.damage}
                  onChange={(event) => handleActionCardChange(card.id, 'damage', event.target.value)}
                  placeholder={card.type === 'Weapon' ? 'Damage / dice' : 'Potency scaling'}
                />
                <TextArea
                  value={card.effect}
                  onChange={(event) => handleActionCardChange(card.id, 'effect', event.target.value)}
                  placeholder='Effects, triggers, or stance multipliers.'
                />
                <TextArea
                  value={card.requirements}
                  onChange={(event) => handleActionCardChange(card.id, 'requirements', event.target.value)}
                  placeholder='Required items, stances, casters, or positioning.'
                />
                <div className='trialment-skill-controls'>
                  <label className='trialment-toggle'>
                    <Input
                      type='checkbox'
                      checked={card.inAction}
                      onChange={() => toggleActionCardFlag(card.id, 'inAction')}
                    />
                    In Action
                  </label>
                  {card.type === 'Weapon' && (
                    <label className='trialment-toggle'>
                      <Input
                        type='checkbox'
                        checked={card.twoHanded}
                        onChange={() => toggleActionCardFlag(card.id, 'twoHanded')}
                      />
                      Two-Handed
                    </label>
                  )}
                </div>
                {card.twoHanded && (
                  <span className='trialment-two-hand-tag'>Occupies both hands</span>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
